import { useEffect, useState, useRef } from 'react';
import { api } from '../api';
import '../styles/extension-connect.css';

type Status = 'generating' | 'waiting' | 'connected' | 'fallback' | 'error';

export function ExtensionConnectPage() {
  const [status, setStatus] = useState<Status>('generating');
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const keyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let fallbackTimer: ReturnType<typeof setTimeout>;

    async function generate() {
      try {
        const date = new Date().toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric'
        });
        const result = await api.createApiKey(`IO Inspector Extension (${date})`);
        if (cancelled) return;

        setApiKey(result.key);
        setStatus('waiting');

        // If extension doesn't pick it up within 6s, show fallback
        fallbackTimer = setTimeout(() => {
          if (!cancelled) setStatus('fallback');
        }, 6000);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to generate API key');
        setStatus('error');
      }
    }

    void generate();

    // Listen for extension confirmation
    const handler = () => {
      if (!cancelled) {
        clearTimeout(fallbackTimer);
        setStatus('connected');
      }
    };
    document.addEventListener('io-extension-connected', handler);

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimer);
      document.removeEventListener('io-extension-connected', handler);
    };
  }, []);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="ext-connect">
      {/* Hidden element for the content script to read */}
      {apiKey && (
        <div id="io-ext-api-key" data-key={apiKey} ref={keyRef} style={{ display: 'none' }} />
      )}

      {status === 'generating' && (
        <div className="ext-connect__card">
          <div className="ext-connect__spinner" />
          <h2>Setting up connection...</h2>
          <p className="ext-connect__hint">Generating API key for the extension.</p>
        </div>
      )}

      {status === 'waiting' && (
        <div className="ext-connect__card">
          <div className="ext-connect__spinner" />
          <h2>Connecting to IO Inspector...</h2>
          <p className="ext-connect__hint">Waiting for the extension to pick up the connection.</p>
        </div>
      )}

      {status === 'connected' && (
        <div className="ext-connect__card">
          <div className="ext-connect__icon">&#10003;</div>
          <h2>Connected!</h2>
          <p className="ext-connect__hint">
            The IO Inspector extension is now connected to your account.
            You can close this tab.
          </p>
        </div>
      )}

      {status === 'fallback' && (
        <div className="ext-connect__card">
          <h2>Almost there</h2>
          <p className="ext-connect__hint">
            The extension didn't pick up the connection automatically.
            Copy this API key and paste it in the extension Settings page.
          </p>
          <div className="ext-connect__key-box">
            <code className="ext-connect__key">{apiKey}</code>
            <button className="btn btn-primary btn-sm" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <p className="ext-connect__hint ext-connect__hint--small">
            Open the extension popup → Settings → paste the key → Save.
          </p>
        </div>
      )}

      {status === 'error' && (
        <div className="ext-connect__card">
          <h2>Something went wrong</h2>
          <p className="ext-connect__error">{error}</p>
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}
